import type {
    ContactFieldLabels,
    HiddenContactFields,
    RequiredContactFields,
    TemplateFormLanguage,
} from '@common/types/templateForms';
import { Box, Stack, TextField, useTheme } from '@mui/material';
import { memo, useState } from 'react';
import ContactInfoSection from '@/pages/QuoteIntakeClientView/IntakeFormView/ContactInfoSection';
import ContactFieldsEditor from './ContactFieldsEditor';

const SECTION_DESCRIPTION_MAX_LENGTH = 2000;

interface Props {
    contactSectionDescription: string;
    language: TemplateFormLanguage;
    hiddenContactFields: HiddenContactFields;
    requiredContactFields: RequiredContactFields;
    contactFieldLabels?: ContactFieldLabels;
    textFieldSx: object;
    onContactSectionDescriptionCommit: (description: string) => void;
    onHiddenContactFieldChange: (field: keyof HiddenContactFields, checked: boolean) => void;
    onRequiredContactFieldChange: (field: keyof RequiredContactFields, checked: boolean) => void;
    onContactFieldLabelsChange: (
        fieldLabels: ContactFieldLabels | undefined,
    ) => void | Promise<void>;
}

function ContactFieldsConfigPanel({
    contactSectionDescription,
    language,
    hiddenContactFields,
    requiredContactFields,
    contactFieldLabels,
    textFieldSx,
    onContactSectionDescriptionCommit,
    onHiddenContactFieldChange,
    onRequiredContactFieldChange,
    onContactFieldLabelsChange,
}: Props) {
    const theme = useTheme();
    const [descriptionDraft, setDescriptionDraft] = useState(contactSectionDescription);

    // Reset the draft when the saved description changes
    const [savedDescription, setSavedDescription] = useState(contactSectionDescription);
    if (contactSectionDescription !== savedDescription) {
        setSavedDescription(contactSectionDescription);
        setDescriptionDraft(contactSectionDescription);
    }

    const commitDescription = () => {
        onContactSectionDescriptionCommit(descriptionDraft);
    };

    return (
        <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            sx={{ marginBottom: '16px', paddingX: '16px', alignItems: 'flex-start' }}
        >
            <Box
                sx={{
                    opacity: 0.5,
                    pointerEvents: 'none',
                    flex: 1,
                    width: '100%',
                }}
            >
                <ContactInfoSection
                    index={0}
                    totalContacts={1}
                    values={{
                        firstName: '',
                        lastName: '',
                        email: '',
                        cellPhone: '',
                    }}
                    onChange={() => {}}
                    hiddenFields={hiddenContactFields}
                    requiredFields={requiredContactFields}
                    fieldLabels={contactFieldLabels}
                    description={descriptionDraft}
                    language={language}
                />
            </Box>
            <Box
                sx={{
                    width: { xs: '100%', lg: '360px' },
                    minWidth: { lg: '360px' },
                    border: `1px solid ${theme.palette.neutral.borderColor}`,
                    borderRadius: '8px',
                    backgroundColor: theme.palette.neutral.white,
                    padding: '16px',
                }}
            >
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Contact section description (optional)"
                    helperText={`Shown at the top of contact fields. ${descriptionDraft.length}/${SECTION_DESCRIPTION_MAX_LENGTH}`}
                    value={descriptionDraft}
                    onChange={(event) => setDescriptionDraft(event.target.value)}
                    onBlur={commitDescription}
                    inputProps={{
                        maxLength: SECTION_DESCRIPTION_MAX_LENGTH,
                        style: { resize: 'vertical' },
                    }}
                    sx={{ ...textFieldSx, marginTop: 0, marginBottom: 2 }}
                />
                <ContactFieldsEditor
                    language={language}
                    hiddenFields={hiddenContactFields}
                    requiredFields={requiredContactFields}
                    fieldLabels={contactFieldLabels}
                    onHiddenFieldChange={onHiddenContactFieldChange}
                    onRequiredFieldChange={onRequiredContactFieldChange}
                    onFieldLabelsChange={onContactFieldLabelsChange}
                />
            </Box>
        </Stack>
    );
}

export default memo(ContactFieldsConfigPanel);
